import { defineStore } from 'pinia';
import { getMainAppMenuList } from '../api/auth';
import { safeStorage } from '../utils/safeLocalStorage';

export interface MenuItem {
  id: string;
  parentId: string;
  weight: number;
  name: string;
  path: string;
  meta: {
    isLink?: string;
    isIframe?: boolean;
    isKeepAlive?: boolean;
    icon: string;
    enName?: string;
    isAffix?: boolean;
    title: string;
    isHide?: boolean;
    description?: string;
  };
  sortOrder: number;
  menuType: string;
  permission?: string | null;
  children?: MenuItem[];
}

export const useMenuStore = defineStore('menu', {
  state: () => ({
    menuList: [] as MenuItem[],
    filteredMenus: [] as MenuItem[],
    isLoading: false,
    currentUserCenterMenu: [] as MenuItem[],
  }),

  getters: {
    availableMenus: (state) => {
      if (state.filteredMenus.length === 0) {
        const store = useMenuStore();
        store.restoreFromCache();
        if (state.filteredMenus.length === 0 && state.menuList.length > 0) {
          store.filterMenus();
        }
      }
      return state.filteredMenus;
    },
    hasMenuData: (state) => {
      // 检查内存或缓存中是否有数据
      if (state.menuList.length > 0) return true;
      
      const cachedMenuList = safeStorage.get<MenuItem[]>('menuList');
      return !!(cachedMenuList && cachedMenuList.length > 0);
    },
    menuCount: (state) => state.menuList.length,
  },

  actions: {
    // 从缓存恢复菜单数据
    restoreFromCache() {
      if (this.menuList.length > 0) return; // 内存中已有数据，不需要恢复
      
      const cachedMenuList = safeStorage.get<MenuItem[]>('menuList');
      const cachedFilteredMenus = safeStorage.get<MenuItem[]>('filteredMenus');
      const cachedUserCenterMenu = safeStorage.get<MenuItem[]>('currentUserCenterMenu');
      
      if (cachedMenuList) {
        this.menuList = cachedMenuList;
        console.log('从缓存恢复菜单数据:', cachedMenuList.length, '条');
      }
      
      if (cachedFilteredMenus) {
        this.filteredMenus = cachedFilteredMenus;
        console.log('从缓存恢复筛选菜单:', cachedFilteredMenus.length, '条');
      }
      
      if (cachedUserCenterMenu) {
        this.currentUserCenterMenu = cachedUserCenterMenu;
        console.log('从缓存恢复用户中心菜单:', cachedUserCenterMenu.length, '条');
      }
    },
    
    // 保存所有菜单数据到缓存
    saveToCache() {
      if (this.menuList.length > 0) {
        safeStorage.set('menuList', this.menuList);
        console.log('保存menuList到缓存:', this.menuList.length, '条');
      }
      
      if (this.filteredMenus.length > 0) {
        safeStorage.set('filteredMenus', this.filteredMenus);
        console.log('保存filteredMenus到缓存:', this.filteredMenus.length, '条');
      }
      
      if (this.currentUserCenterMenu.length > 0) {
        safeStorage.set('currentUserCenterMenu', this.currentUserCenterMenu);
        console.log('保存currentUserCenterMenu到缓存:', this.currentUserCenterMenu.length, '条');
      }
    },
    async getMenuList(params: any) {
      if (this.menuList.length === 0) {
        this.restoreFromCache();
      }
      
      if (this.menuList.length > 0) {
        this.filterMenus();
        this.saveToCache();
        return this.menuList;
      }

      try {
        this.isLoading = true;
        const res = await getMainAppMenuList(params);
        this.menuList = Array.isArray(res?.data) ? (res.data as MenuItem[]) : [];
        
        const userCenter = this.menuList.find(m => m.name === '统一用户中心');
        if (userCenter) {
          console.log('统一用户中心children:', userCenter.children);
        } else {
          console.warn('❌ 未找到统一用户中心');
        }

        this.filterMenus();
        
        // 保存所有菜单数据到缓存
        this.saveToCache();
        
        return this.menuList;
      } catch (error) {
        console.error('获取主应用菜单失败:', error);
        this.menuList = [];
        this.filteredMenus = [];
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    filterMenus() {
      if (!this.menuList.length) {
        this.filteredMenus = [];
        console.log('filterMenus - menuList为空，直接返回');
        return;
      }

      this.filteredMenus = this.menuList;
      console.log('筛选后的menu:', this.filteredMenus);
      
      // 保存筛选结果到缓存
      if (this.filteredMenus.length > 0) {
        safeStorage.set('filteredMenus', this.filteredMenus);
      }
    },

    getAvailableMenus() {
      // 优先从内存获取
      if (this.filteredMenus.length === 0) {
        this.restoreFromCache();
      }
      return this.filteredMenus;
    },

    isMenuAvailable(menuName: string) {
      return this.filteredMenus.some(item => item.name === menuName);
    },

    clearMenus() {
      this.menuList = [];
      this.filteredMenus = [];
      this.currentUserCenterMenu = [];
      
      // 清除缓存的所有菜单数据
      safeStorage.remove('menuList');
      safeStorage.remove('filteredMenus');
      safeStorage.remove('currentUserCenterMenu');
      
      console.log('已清除所有菜单数据（内存+缓存）');
    },

    getMenuChildren(menuName: string) {
      // 优先从内存获取
      if (!this.menuList || this.menuList.length === 0) {
        console.warn('内存中没有菜单数据，尝试从缓存恢复');
        this.restoreFromCache();
      }
      
      if (!this.menuList || this.menuList.length === 0) {
        console.warn('❌ 菜单数据为空！');
        return [];
      }

      const menuItem = this.menuList.find(menu => {
        return menu.name === menuName || menu.meta.title === menuName;
      });

      if (menuItem) {
        console.log('children内容:', menuItem.children);
      } else {
        console.warn('❌ 未找到匹配菜单！');
        console.log('可用的菜单项:', this.menuList.map(m => ({ name: m.name, title: m.meta.title })));
      }
      return menuItem?.children || [];
    },

    setCurrentUserCenterMenu(children: MenuItem[]) {
      this.currentUserCenterMenu = children;
      // 同时保存到缓存
      if (children.length > 0) {
        safeStorage.set('currentUserCenterMenu', children);
        console.log('保存用户中心菜单到缓存:', children.length, '条');
      }
    },

    createMenuActions() {
      return {
        getMenuChildren: this.getMenuChildren.bind(this),
        getAvailableMenus: this.getAvailableMenus.bind(this),
        isMenuAvailable: this.isMenuAvailable.bind(this),
      };
    },
  },
});
